import React from 'react';
import { 
  Box, 
  Typography, 
  IconButton,
  FormControl,
  MenuItem,
  Select,
  Slider,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { AudioPlayerBar } from '../styles/componentStyles';
import { AudioPlayingIndicator } from './AudioPlayingIndicator';

const PLAYBACK_RATES = [0.75, 1, 1.25, 1.5];

// Format time as MM:SS for audio player
const formatAudioTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const IELTSAudioPlayer = ({
  audioProgress,
  isPlaying,
  currentTime,
  duration,
  isMuted,
  togglePlay,
  skipForward,
  skipBackward,
  seekTo,
  toggleMute,
  playbackRate = 1,
  setPlaybackRate = () => {},
  canChangeSpeed = false,
  isSimulationMode = false,
  isReviewMode = false,
  canSeek = !isSimulationMode && !isReviewMode,
  sx = {}
}) => {
  const handleSeekChange = (_, value) => {
    if (!canSeek || !seekTo || !duration) return;
    seekTo(value);
  };

  return (
    <AudioPlayerBar sx={{ 
      flexShrink: 0, 
      py: 0.8, 
      px: 3, 
      borderBottom: '2px solid #E8DFFF',
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: 'rgba(153, 108, 254, 0.02)',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        width: `${audioProgress}%`,
        backgroundColor: 'rgba(153, 108, 254, 0.08)',
        transition: 'width 0.6s linear',
        zIndex: 0,
      },
      ...sx
    }}>

      {!isSimulationMode && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', zIndex: 1 }}>
          <IconButton 
            onClick={skipBackward} 
            size="small"
            sx={{ color: '#666' }}
          >
            <Box component="span" sx={{ fontSize: '20px' }}>⟲</Box>
          </IconButton>
          
          <IconButton 
            onClick={togglePlay} 
            size="medium"
            sx={{ 
              backgroundColor: '#996CFE', 
              color: 'white',
              width: 44,
              height: 44,
              '&:hover': { backgroundColor: '#7C4DFF' },
              animation: isPlaying ? 'pulseButton 2s ease-in-out infinite' : 'none',
              '@keyframes pulseButton': {
                '0%, 100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(153, 108, 254, 0.4)' },
                '50%': { transform: 'scale(1.05)', boxShadow: '0 0 0 10px rgba(153, 108, 254, 0)' }
              }
            }}
          >
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
          
          <IconButton 
            onClick={skipForward} 
            size="small"
            sx={{ color: '#666' }}
          >
            <Box component="span" sx={{ fontSize: '20px' }}>⟳</Box>
          </IconButton>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, minWidth: '60px', position: 'relative', zIndex: 1 }}>
        <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>
          {formatAudioTime(currentTime)}
        </Typography>
      </Box>
      
      {canSeek ? (
        <Box sx={{ flex: 1, mx: 2, position: 'relative', zIndex: 1 }}>
          <Slider
            value={Math.min(currentTime || 0, duration || 0)}
            min={0}
            max={duration || 0}
            step={1}
            onChange={handleSeekChange}
            disabled={!duration}
            aria-label="Audio progress"
            sx={{
              color: '#996CFE',
              height: 6,
              '& .MuiSlider-rail': {
                opacity: 1,
                backgroundColor: '#E8DFFF',
              },
              '& .MuiSlider-track': {
                border: 'none',
                background: 'linear-gradient(90deg, #996CFE 0%, #7C4DFF 100%)',
              },
              '& .MuiSlider-thumb': {
                width: 18,
                height: 18,
                backgroundColor: '#fff',
                border: '3px solid #996CFE',
                boxShadow: '0 4px 12px rgba(153, 108, 254, 0.28)',
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0 0 0 8px rgba(153, 108, 254, 0.14)',
                },
              },
            }}
          />
        </Box>
      ) : (
        <Box sx={{ flex: 1 }} />
      )}
      
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, minWidth: '60px', flexDirection: 'column', gap: 0.5, position: 'relative', zIndex: 1 }}>
        <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>
          {formatAudioTime(duration)}
        </Typography>
      </Box>

      {canChangeSpeed && (
        <FormControl size="small" sx={{ minWidth: 72, mr: 1, position: 'relative', zIndex: 1 }}>
          <Select
            value={playbackRate}
            onChange={(event) => setPlaybackRate(Number(event.target.value))}
            inputProps={{ 'aria-label': 'Playback speed' }}
            sx={{
              height: 32,
              fontSize: '0.8rem',
              color: '#666',
              '& .MuiSelect-select': { py: 0.5, px: 1 },
            }}
          >
            {PLAYBACK_RATES.map((rate) => (
              <MenuItem key={rate} value={rate}>{rate}x</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      
      {!isSimulationMode && (
        <IconButton
          onClick={toggleMute}
          size="small"
          sx={{ color: '#666', position: 'relative', zIndex: 1 }}
        >
          {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
        </IconButton>
      )}
      
      <Box sx={{ ml: 2, display: { xs: 'none', sm: 'flex' }, alignItems: 'center', position: 'relative', zIndex: 1 }}>
        <AudioPlayingIndicator isPlaying={isPlaying} />
      </Box>
    </AudioPlayerBar>
  );
};

export default IELTSAudioPlayer;
